import {
  TNotificationOrigin,
  OFFICE_NOTIFICATION_KEYS,
  TOfficeNotificationKey,
} from './types';

/** UTM source marking an email landing (`utm_source=email`). */
export const UTM_SOURCE_PARAM = 'utm_source';
/** UTM campaign carrying which notification email was clicked (`utm_campaign=<key>`). */
export const UTM_CAMPAIGN_PARAM = 'utm_campaign';
/** CSV of the shift ids the CTA was built for (`shift_ids=a,b,c`). */
export const SHIFT_IDS_PARAM = 'shift_ids';
/** Professional id, carried on candidate-applied CTAs only (`professional_id=<id>`). */
export const PROFESSIONAL_ID_PARAM = 'professional_id';

const KNOWN_ORIGINS: readonly string[] = ['email'];

export interface IParsedNotificationParams {
  /** Present only when the link carried a recognized origin (`utm_source`). */
  origin?: TNotificationOrigin;
  /** Present only when the link carried a recognized notification (`utm_campaign`). */
  notification?: TOfficeNotificationKey;
  /** Shift ids from the `shift_ids` CSV. Present only when at least one is supplied. */
  shiftIds?: string[];
  /** Professional id from `professional_id`. Present only on candidate-applied links. */
  professionalId?: string;
}

/**
 * Reads and validates the office-notification params off an email CTA link,
 * per the UTM contract resolved in GOTU-4135. Unknown/absent values are dropped
 * so callers can treat a missing `origin` as "not an email landing" and a
 * missing `notification` as "fall back to the page's default event".
 */
export function parseNotificationParams(
  searchParams: URLSearchParams,
): IParsedNotificationParams {
  const result: IParsedNotificationParams = {};

  const origin = searchParams.get(UTM_SOURCE_PARAM);
  if (origin && KNOWN_ORIGINS.includes(origin)) {
    result.origin = origin as TNotificationOrigin;
  }

  const notification = searchParams.get(UTM_CAMPAIGN_PARAM);
  if (
    notification &&
    (OFFICE_NOTIFICATION_KEYS as readonly string[]).includes(notification)
  ) {
    result.notification = notification as TOfficeNotificationKey;
  }

  const shiftIds = searchParams
    .get(SHIFT_IDS_PARAM)
    ?.split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  if (shiftIds && shiftIds.length > 0) {
    result.shiftIds = shiftIds;
  }

  const professionalId = searchParams.get(PROFESSIONAL_ID_PARAM)?.trim();
  if (professionalId) {
    result.professionalId = professionalId;
  }

  return result;
}
